// Live's actions on a My Generations image card (`useGeneratedImageCardActions.tsx`): Save,
// Save to project, Download, Tweak, Edit layers on a finished Layerize's card (spec G13b) and, on a
// saved picture of a simple generation, Save Prompt (spec G12). Live's Layerize on a card sits
// behind its capability flag, so it is not shown.
import { ActionIcon, Group, Tooltip } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { Check, Download, FolderOpen, Save, SquarePen, Type, WandSparkles } from "lucide-react";

import type { GenerationOutput, Job } from "../../api/generated/model";
import { SIMPLE_TYPES, type SavePrompt } from "../../platform/media/save-prompt";

export interface ImageCardActionsProps {
  teamId: string;
  brandId: string;
  job: Job;
  output: GenerationOutput;
  /** The card's place in its row, from 1: what its controls are named by. */
  number: number;
  /** Kept in the library: by the server's read, or by a save of this page. */
  saved: boolean;
  saving: boolean;
  onSave: () => void;
  onSaveToProject: () => void;
  onDownload: () => void;
  /** Live's "Edit layers": the layer editor on this finished Layerize. */
  onEditLayers: () => void;
  prompt: SavePrompt;
}

/** The card's pill (design-system.md 3.3), in live's order. */
export function ImageCardActions(props: ImageCardActionsProps) {
  const { teamId, brandId, job, output, number, saved, saving, prompt } = props;
  const picture = output.saved_asset_id ?? null;
  const promptable = picture !== null && SIMPLE_TYPES.includes(job.type);
  return (
    <Group gap={4} wrap="nowrap">
      <Tooltip label={saved ? "Saved" : "Save"}>
        <ActionIcon
          variant="text-white"
          aria-label={saved ? `Image ${number} saved` : `Save image ${number}`}
          disabled={saved || saving}
          onClick={props.onSave}
        >
          {saved ? <Check size={20} aria-hidden /> : <Save size={20} strokeWidth={1.5} />}
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Save to project">
        <ActionIcon
          variant="text-white"
          aria-label={`Save image ${number} to project`}
          disabled={saved || saving}
          onClick={props.onSaveToProject}
        >
          <FolderOpen size={20} strokeWidth={1.5} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Download">
        <ActionIcon
          variant="text-white"
          aria-label={`Download image ${number}`}
          onClick={props.onDownload}
        >
          <Download size={20} strokeWidth={1.5} />
        </ActionIcon>
      </Tooltip>
      {/* Live's Tweak: the composer changes this picture (our "Change this image"). */}
      <Tooltip label="Tweak">
        <ActionIcon
          variant="text-white"
          aria-label={`Tweak image ${number}`}
          renderRoot={(rootProps) => (
            <Link
              {...rootProps}
              to="/teams/$teamId/brands/$brandId/generate"
              params={{ teamId, brandId }}
              search={{ type: "image.tweak", job: job.id, output: output.index }}
            />
          )}
        >
          <WandSparkles size={20} strokeWidth={1.5} />
        </ActionIcon>
      </Tooltip>
      {job.type === "image.layerize" && job.status === "succeeded" ? (
        <Tooltip label="Edit layers">
          <ActionIcon variant="text-white" aria-label={`Edit the layers of image ${number}`} onClick={props.onEditLayers}>
            <SquarePen size={20} strokeWidth={1.5} />
          </ActionIcon>
        </Tooltip>
      ) : null}
      {promptable ? (
        // Live's icon has no saved look: the row says live's toast, or its 409 (issue 908).
        <Tooltip label="Save Prompt">
          <ActionIcon
            variant="text-white"
            aria-label={`Save the prompt of image ${number}`}
            loading={prompt.pending === picture}
            onClick={() => prompt.save(picture)}
          >
            <Type size={20} strokeWidth={1.5} />
          </ActionIcon>
        </Tooltip>
      ) : null}
    </Group>
  );
}
